import { Suspense, useEffect, useMemo } from "react"
import DonationsList from "./components/DonationsList"
import InKindDonationsList from "./components/InKindDonationsList"
import { Heading, Checkbox } from "@planningcenter/doxy-web"
import DonationTypeSelector from "./components/DonationTypeSelector"
import { Loading } from "source/shared/components"
import { useDonationsSummaryContext } from "../hooks/useDonationSummaryContext"
import DonationYearFilter from "./components/DonationYearFilter"
import DonationFundFilter from "./components/DonationFundFilter"
import generateDownloadUrl from "../utils/generateDownloadUrl"
import { useDonationHistoryContext } from "../hooks/useDonationHistoryContext"
import { NotifyNativeLoaded } from "source/shared/hooks/useNotifyNativeLoaded"
import NativeHeader from "source/shared/NativeHeader"

export default function DonationHistory() {
  const { donationsSummary } = useDonationsSummaryContext()
  const donationYears = donationsSummary.all_donation_years
  const donationFunds = donationsSummary.all_donation_funds
  const hasInKindDonations = donationsSummary.has_in_kind_donations

  const {
    activeYear,
    setActiveYear,
    activeDonationType,
    setActiveDonationType,
    activeFund,
    setActiveFund,
    hideNonDeductible,
    setHideNonDeductible,
    downloadUrl,
    setDownloadUrl,
  } = useDonationHistoryContext()

  const generatedUrl = useMemo(() => {
    return generateDownloadUrl({
      year: activeYear,
      fund: activeFund,
      donationType: activeDonationType,
      hideNonDeductibleDonations: hideNonDeductible,
    })
  }, [activeYear, activeFund, activeDonationType, hideNonDeductible])

  useEffect(() => {
    if (generatedUrl !== downloadUrl) {
      setDownloadUrl(generatedUrl)
    }
  }, [generatedUrl, downloadUrl, setDownloadUrl])

  return (
    <>
      <NativeHeader title="Donation history" />
      <section>
        <div className="d-f fd-c g-2">
          <div className="d-f ai-c jc-sb">
            <div className="d-n d-f@md">
              <Heading level={1} size={2} text="Donation history" />
            </div>
            {hasInKindDonations && (
              <DonationTypeSelector
                activeDonationType={activeDonationType}
                setActiveDonationType={(type) => setActiveDonationType(type)}
              />
            )}
          </div>
          <div className="d-f ai-c jc-sb g-1">
            {donationYears && (
              <DonationYearFilter
                donationYears={donationYears}
                activeDonationYear={activeYear}
                onChange={(year) => setActiveYear(year)}
              />
            )}
            {donationFunds && (
              <DonationFundFilter
                donationFunds={donationFunds}
                activeDonationFund={activeFund}
                onChange={(fund) => setActiveFund(fund)}
              />
            )}
          </div>
          {activeDonationType === "monetary" && (
            <div className="d-f ai-c">
              <Checkbox
                id="hide-non-deductible-donations"
                label="Hide nondeductible donations"
                checked={hideNonDeductible}
                onChange={() => setHideNonDeductible(!hideNonDeductible)}
              />
            </div>
          )}
          <Suspense fallback={<Loading />}>
            <NotifyNativeLoaded />
            <div className="d-f fd-c g-2">
              {activeDonationType === "monetary" ? (
                <DonationsList
                  donationYears={donationYears}
                  activeYear={activeYear}
                  activeFund={activeFund}
                  hideNondeductibleDonations={hideNonDeductible}
                  downloadUrl={downloadUrl}
                />
              ) : (
                activeDonationType === "inKind" && (
                  <InKindDonationsList
                    donationYears={donationYears}
                    activeYear={activeYear}
                    activeFund={activeFund}
                    downloadUrl={downloadUrl}
                  />
                )
              )}
            </div>
          </Suspense>
        </div>
      </section>
    </>
  )
}
